import { styled } from '@mui/material/styles'
import Box from '@/components/common/Box'
import {
	colors,
	typography,
	typePresets,
	spacing,
	radius,
	shadows,
	sizing,
	transitions,
	focusVisible,
} from '@/styles/designTokens'

export const CardRoot = styled('article', {
	shouldForwardProp: (prop) => prop !== 'isOutOfStock' && prop !== 'isReOrder',
})<{ isOutOfStock?: boolean; isReOrder?: boolean }>(({ theme, isOutOfStock, isReOrder }) => ({
	boxSizing: 'border-box',
	position: 'relative',
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'stretch',
	width: '100%',
	border: `1px solid ${isReOrder ? colors.textSecondary : colors.border}`,
	borderRadius: radius.card,
	boxShadow: shadows.card,
	overflow: 'hidden',
	backgroundColor: isOutOfStock ? colors.surfaceMuted : colors.surfaceWhite,
	transition: `background-color ${transitions.fast}`,
	height: isReOrder ? sizing.cardHeightOA : sizing.cardHeightMobile,
	minHeight: isReOrder ? sizing.cardHeightOA : sizing.cardHeightMobile,
	maxHeight: isReOrder ? sizing.cardHeightOA : sizing.cardHeightMobile,
	cursor: isOutOfStock ? 'default' : 'pointer',
	[theme.breakpoints.up('lg')]: {
		height: isReOrder ? sizing.cardHeightOA : sizing.cardHeightDesktop,
		minHeight: isReOrder ? sizing.cardHeightOA : sizing.cardHeightDesktop,
		maxHeight: isReOrder ? sizing.cardHeightOA : sizing.cardHeightDesktop,
	},
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.brand}`,
		outlineOffset: focusVisible.outlineOffset,
	},
}))

export const LoadingOverlay = styled('div')(({ theme }) => ({
	position: 'fixed',
	inset: 0,
	zIndex: theme.zIndex.modal,
	backgroundColor: colors.overlayPageLoading,
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	gap: spacing.md,
}))

export const LoadingText = styled('p')({
	margin: 0,
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightRegular,
	color: colors.textPrimary,
})

export const CardBody = styled(Box)({
	flex: '1 1 auto',
	minWidth: 0,
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	padding: spacing.lg,
})

export const CardInfo = styled(Box)({
	flex: '1 1 auto',
	display: 'flex',
	flexDirection: 'column',
	gap: spacing.xs,
})

export const CardName = styled('h3', {
	shouldForwardProp: (prop) => prop !== 'isOutOfStock',
})<{ isOutOfStock?: boolean }>(({ isOutOfStock }) => ({
	margin: 0,
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	color: isOutOfStock ? colors.textSecondary : colors.textPrimary,
	display: '-webkit-box',
	WebkitLineClamp: 2,
	WebkitBoxOrient: 'vertical',
	overflow: 'hidden',
	wordBreak: 'break-word',
}))

export const CardDescription = styled('p', {
	shouldForwardProp: (prop) => prop !== 'isOutOfStock' && prop !== 'isReOrder',
})<{ isOutOfStock?: boolean; isReOrder?: boolean }>(({ isOutOfStock, isReOrder }) => ({
	margin: 0,
	fontSize: typography.fontSizeSm,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightSm,
	letterSpacing: typography.letterSpacingWidest,
	color: isOutOfStock ? colors.textDisabled : colors.textSecondary,
	display: '-webkit-box',
	WebkitLineClamp: isReOrder ? 1 : 2,
	WebkitBoxOrient: 'vertical',
	overflow: 'hidden',
}))

export const CardPriceRow = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	gap: spacing.sm,
	marginTop: spacing.md,
})

export const CardPrice = styled('span', {
	shouldForwardProp: (prop) => prop !== 'isOutOfStock',
})<{ isOutOfStock?: boolean }>(({ isOutOfStock }) => ({
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightMd,
	color: isOutOfStock ? colors.textSecondary : colors.textPrimary,
}))

export const SoldOutLabel = styled('span')({
	...typePresets.bodySm,
	fontWeight: typography.fontWeightSemiBold,
	color: colors.errorFigma,
})

export const LowStockLabel = styled('span')({
	...typePresets.bodySm,
	fontWeight: typography.fontWeightSemiBold,
	color: colors.warningFigma,
})

export const CardImageWrap = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'isReOrder',
})<{ isReOrder?: boolean }>(({ theme, isReOrder }) => ({
	width: isReOrder ? sizing.cardImageOAWidth : sizing.cardImageWidth,
	flexShrink: 0,
	alignSelf: isReOrder ? 'stretch' : undefined,
	height: isReOrder ? undefined : sizing.cardHeightMobile,
	position: 'relative',
	backgroundColor: colors.overlayLight,
	[theme.breakpoints.up('lg')]: {
		height: isReOrder ? undefined : sizing.cardHeightDesktop,
	},
}))

export const CardImage = styled('img', {
	shouldForwardProp: (prop) => prop !== 'isReOrder' && prop !== 'isOutOfStock',
})<{ isReOrder?: boolean; isOutOfStock?: boolean }>(({ isReOrder, isOutOfStock }) => ({
	position: isReOrder ? 'absolute' : undefined,
	inset: isReOrder ? 0 : undefined,
	width: '100%',
	height: '100%',
	objectFit: 'cover',
	display: 'block',
	filter: isOutOfStock ? 'grayscale(1)' : undefined,
}))

export const CardImagePlaceholder = styled(Box)({
	width: '100%',
	height: '100%',
	backgroundColor: colors.overlayLight,
})

export const AddButton = styled('button', {
	shouldForwardProp: (prop) => prop !== 'isOutOfStock' && prop !== 'isReOrder',
})<{ isOutOfStock?: boolean; isReOrder?: boolean }>(({ isOutOfStock, isReOrder }) => ({
	position: 'absolute',
	bottom: isReOrder ? sizing.addButtonOffsetReOrder : sizing.addButtonOffset,
	right: sizing.addButtonOffset,
	width: sizing.addButton,
	height: sizing.addButton,
	borderRadius: radius.circle,
	border: `1px solid ${colors.overlayHeavy}`,
	backgroundColor: isReOrder ? colors.overlaySubtle : colors.surfaceWhite,
	color: colors.textPrimary,
	cursor: 'pointer',
	display: isOutOfStock ? 'none' : 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	padding: 0,
	'& svg': { fontSize: typography.fontSizeXl },
	'&:disabled': {
		opacity: 0.6,
		cursor: 'default',
	},
	'&:focus-visible': {
		outline: focusVisible.outline,
		outlineOffset: focusVisible.outlineOffset,
	},
}))
